import { AccountAvatar } from "entities/accounts";
import { useDispatch, useSelector } from "react-redux";
import { ConversationAvatar } from "../../conversation-avatar";

import { MessageStatus } from "types/conversationInbox";
import {
  BlockContainer,
  ConversationAvatarWrapper,
  MessageContainer,
  MessageHiddenNote,
  RetryContainer,
  RootContainer,
} from "./styled";
import { renderContent } from "./message-types/message-content-render";
import { ServerMessageInfoProps } from "./types";
import { ReplyToBlock } from "./reply-to-block";
import { retryCreateMessage } from "actions/conversationInbox";
import { SkedIcon } from "ui";
import { DateLabel } from "../components/date-label";
import { SentimentBadge } from "./sentiment-badge";
import { ReactionsChangeData } from "../components/message-reactions";
import { Collaborator } from "features/user";
import {
  getConversationAccountContext,
  WrappedInboxState,
} from "selectors/conversationInboxSelectors";
import { cn } from "@/lib/utils";
import { MessageGroupingProps } from "../components/messages-layout/messages-layout-common";

export interface AccountMessageProps extends ServerMessageInfoProps {
  userName: string;
  isTeamMessage?: boolean;
  isCurrentUser: boolean;
  collaborators: Collaborator[];
  interlocutorId: string;
  status?: MessageStatus;
  shouldDisplaySender: boolean;
  forceLeft?: boolean;
  onReactionsChange?: (data: ReactionsChangeData) => void;
  messageGrouping?: MessageGroupingProps;
}

export function AccountMessage({
  conversationId,
  message = "",
  timestamp,
  userName,
  src,
  isTeamMessage = false,
  collaborators,
  story,
  attachments,
  type,
  name,
  replyTo,
  interlocutorId,
  status,
  reactions,
  messageId,
  onReactionsChange,
  attachment,
  shouldDisplaySender,
  originalMessage,
  forceLeft,
  conversation,
  messageGrouping,
}: AccountMessageProps): JSX.Element {
  const dispatch = useDispatch();
  const newMessage = isTeamMessage
    ? getTeamMessage({ message, collaborators })
    : message;

  const onRetry = () => {
    dispatch(
      retryCreateMessage(conversationId, isTeamMessage, messageId, message)
    );
  };

  const convAccount = useSelector((state: WrappedInboxState) =>
    getConversationAccountContext(state, conversationId)
  );

  forceLeft = !!forceLeft;
  const avatar = isTeamMessage ? (
    <ConversationAvatar src={src} name={userName} showInitial />
  ) : (
    <AccountAvatar size="small" />
  );
  return (
    <RootContainer
      left={forceLeft}
      isFirstInGroup={messageGrouping?.isFirstInGroup}
    >
      {messageGrouping?.isFirstInGroup && isTeamMessage && (
        <span
          className={cn(
            "text-xs font-medium text-ink-secondary pb-0.5",
            forceLeft ? "pl-[50px]" : "pr-[50px] text-right"
          )}
        >
          {userName}
        </span>
      )}
      <MessageContainer
        left={forceLeft}
        opaque={!!originalMessage?.deleted || !!originalMessage?.hidden}
      >
        {forceLeft && (
          <ConversationAvatarWrapper
            className={cn(!messageGrouping?.isFirstInGroup && "invisible")}
          >
            {avatar}
          </ConversationAvatarWrapper>
        )}
        <BlockContainer>
          {replyTo && (
            <ReplyToBlock
              message={replyTo.message}
              name={replyTo.name}
              type={replyTo.type}
              story={replyTo.story}
              attachments={replyTo.attachments}
              isLeft={false}
              isCurrentSMUMessage={false}
              isReplyToSMUMessage={replyTo.sender?.id == interlocutorId}
              messageId={replyTo.messageId}
              originalMessage={replyTo.originalMessage}
              conversationId={conversationId}
            />
          )}
          {renderContent({
            type,
            name,
            story,
            attachments,
            message: newMessage,
            isTeamMessage,
            isLeft: forceLeft,
            isRepliedMessage: false,
            reactions,
            onReactionsChange,
            messageId,
            attachment,
            originalMessage,
            conversationId,
            status,
            conversation,
            accountContext: convAccount,
          })}
        </BlockContainer>
        {!forceLeft && (
          <ConversationAvatarWrapper
            className={cn(!messageGrouping?.isFirstInGroup && "invisible")}
          >
            {avatar}
          </ConversationAvatarWrapper>
        )}
      </MessageContainer>
      {messageGrouping?.isLastInGroup && (
        <div className="flex items-center gap-1.5">
          <DateLabel
            forceLeft={forceLeft}
            type={type}
            userName={userName}
            timestamp={timestamp}
            shouldDisplaySender={shouldDisplaySender}
            isUnknownUser={originalMessage?.sender?.isUnknownSource}
          />
          <SentimentBadge sentiment={originalMessage?.sentiment} />
        </div>
      )}
      {originalMessage?.hidden && (
        <MessageHiddenNote>Message has been hidden</MessageHiddenNote>
      )}
      {status === MessageStatus.failed && (
        <RetryContainer onLeft={forceLeft}>
          <SkedIcon icon="info-light" color="red" />
          Failed to deliver message.
          <button
            className="bg-transparent border-none p-0 text-[#fa4f38] underline cursor-pointer ml-[3px] text-inherit transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
            onClick={onRetry}
            aria-label="Retry sending message"
          >
            Retry
          </button>
        </RetryContainer>
      )}
    </RootContainer>
  );
}

interface TeamMessageProps {
  message: string;
  collaborators: Collaborator[];
}

// Helper function to escape HTML to prevent XSS
export const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replaceAll(/[&<>"']/g, (char) => map[char]);
};

export const getTeamMessage = ({
  message,
  collaborators,
}: TeamMessageProps): JSX.Element => {
  // First, escape the entire message to prevent XSS
  const escapedMessage = escapeHtml(message);

  // eslint-disable-next-line no-useless-escape
  const regex = /@[\[]\w+[\]]/gm;
  const newMessage = escapedMessage.replaceAll(regex, (text: string) => {
    const collaborator = collaborators.find((col: Collaborator) =>
      text.includes(col._id)
    );

    const name = collaborator
      ? `${collaborator.firstName}${
          collaborator.lastName ? `_${collaborator.lastName}` : ""
        }`
      : "unknown";
    // Name comes from collaborator data (not from the message text), so it needs
    // its own escaping for safe insertion into dangerouslySetInnerHTML
    const mention = `<span style="color: var(--accent-blurple)">@${escapeHtml(name)}</span>`;
    return mention;
  });

  return <div dangerouslySetInnerHTML={{ __html: newMessage }} />;
};
